import { Box, Button, Typography, LinearProgress } from "@mui/material";
import React from "react";
import bottomPattern from "./utils/svg/Bottom.svg";
import topPattern from "./utils/svg/Top.svg";
import ErrorPage from "./utils/img/ErrorPage.png";
import { Image } from "@aws-amplify/ui-react";
import { clearCacheAndReloadForVerification } from "./utils/clearCacheAndReload";
import { postControlError } from "./services/ApiGraphql";
import { getClientInfo } from "./utils/getClientInfo";

const AUTO_RELOAD_SECONDS = 15;
type Props = React.PropsWithChildren<{ user?: any }>;
type State = { hasError: boolean; error?: Error | null; autoReloadSecondsLeft: number | null };

// Aquí indicamos que props son las props por defecto MÁS children
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, autoReloadSecondsLeft: null };
  private autoReloadTimer: number | null = null;

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  async componentDidCatch(error: Error, info: React.ErrorInfo) {
     const userId = this.props.user?.userId;
     const clientInfo = getClientInfo();          
     const servicio = `ErrorBoundary@${window.location.pathname}`;  

    console.error("ErrorBoundary caught", error, info);
    try {
      window.dispatchEvent(new CustomEvent("app:fatalError", { detail: { message: String(error.message || "Error desconocido"), source: "errorBoundary" } }));
    } catch { }

    this.startAutoReloadCountdown();
  }

  componentWillUnmount() {
    if (this.autoReloadTimer !== null) {
      window.clearInterval(this.autoReloadTimer);
      this.autoReloadTimer = null;
    }
  }

  private startAutoReloadCountdown() {
    if (this.autoReloadTimer !== null) return; // ya iniciado

    this.setState({ autoReloadSecondsLeft: AUTO_RELOAD_SECONDS });

    this.autoReloadTimer = window.setInterval(() => {
      this.setState((prev) => {
        if (prev.autoReloadSecondsLeft === null) {
          return prev;
        }

        if (prev.autoReloadSecondsLeft <= 1) {
          // Último segundo: limpiamos intervalo y recargamos
          if (this.autoReloadTimer !== null) {
            window.clearInterval(this.autoReloadTimer);
            this.autoReloadTimer = null;
          }

          clearCacheAndReloadForVerification();
          return { ...prev, autoReloadSecondsLeft: 0 };
        }

        return {
          ...prev,
          autoReloadSecondsLeft: prev.autoReloadSecondsLeft - 1,
        };
      });
    }, 1000);
  }

  private handleManualReload = () => {
    if (this.autoReloadTimer !== null) {
      window.clearInterval(this.autoReloadTimer);
      this.autoReloadTimer = null;
    }
    clearCacheAndReloadForVerification();
  };

  render() {
    if (this.state.hasError) {
      const secondsLeft =
        this.state.autoReloadSecondsLeft ?? AUTO_RELOAD_SECONDS;
      const progressValue =
        (secondsLeft / AUTO_RELOAD_SECONDS) * 100; // va disminuyendo

      return (
        <>
          <Box
            sx={{
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
              backgroundImage: "#FFFF",
              backgroundSize: "cover",
            }}
          >
            {/* Figura geométrica superior */}
            <img
              src={topPattern}
              alt="Top Pattern"
              style={{
                position: "absolute",
                top: "-150px",
                left: 0,
                width: "100%",
                height: "auto",
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 1.5,
                textAlign: "center",
                zIndex: 5,
              }}
            >
              <Image
                style={{ width: 350, height: "auto" }}
                alt="Error Page"
                src={ErrorPage}
              />

              <Typography
                sx={{
                  color: "#336B87",
                  textAlign: "center",
                  fontFamily: "Montserrat",
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  mt: 3,
                }}
                variant="h3"
              >
                Ups... ALGO NO CARGÓ COMO ESPERÁBAMOS.
              </Typography>

              <Typography
                sx={{
                  color: "rgba(0,0,0,0.6)",
                  textAlign: "center",
                  fontFamily: "Montserrat",
                  fontSize: "1.2rem",
                  mt: 3,
                }}
                variant="h3"
              >
                La carga falló, pero no te preocupes: estamos trabajando para
                que todo vuelva a fluir.
              </Typography>
              <Typography
                sx={{
                  color: "rgba(0,0,0,0.6)",
                  textAlign: "center",
                  fontFamily: "Montserrat",
                  fontSize: "1.2rem",
                  fontWeight: 550,
                }}
                variant="h3"
              >
                Puedes refrescar la página o volver más tarde.
              </Typography>

              <Typography
                sx={{
                  color: "rgba(0,0,0,0.4)",
                  textAlign: "center",
                  fontFamily: "Montserrat",
                  fontSize: "1rem",
                  textTransform: "capitalize",
                  mt: 3,
                  mb: 2,
                }}
                variant="h3"
              >
                UmmIA sigue contigo.
              </Typography>

              {/* Barra de cuenta regresiva + texto */}
              {this.state.autoReloadSecondsLeft !== null && (
                <Box sx={{ width: "280px", mt: 1, mb: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: "0.9rem",
                      color: "rgba(0,0,0,0.6)",
                      mb: 0.5,
                    }}
                  >
                   La página se recargará en {secondsLeft} segundos...
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progressValue}
                    sx={{
                      height: 8,
                      borderRadius: 5,
                      backgroundColor: "rgba(51, 107, 135, 0.15)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 5,
                        backgroundColor: "#336B87",
                      },
                    }}
                  />
                </Box>
              )}

              <Button
                onClick={this.handleManualReload}
                sx={{
                  mt: 4,
                  px: { xs: 5, sm: 7 },
                  py: { xs: 2.2, sm: 2.6 },
                  borderRadius:3,
                  backgroundColor: "#336B87",
                  color: "#fff",
                  fontFamily: "Montserrat",
                  fontSize: { xs: "1.05rem", sm: "1.25rem" },
                  fontWeight: 800,
                  textTransform: "none",
                  letterSpacing: "0.04em",
                  boxShadow: "0 18px 38px rgba(0,0,0,0.35)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  cursor: "pointer",
                  animation:
                    "pulseReloadStrong 1.2s ease-in-out infinite, floatReload 2s ease-in-out infinite",
                  transition:
                    "transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease",

                  "&:hover": {
                    backgroundColor: "#28546A",
                    color: "#fff",
                    filter: "brightness(1.05)",
                    boxShadow: "0 22px 46px rgba(0,0,0,0.45)",
                    transform: "translateY(-2px) scale(1.04)",
                  },
                  "&:active": {
                    transform: "translateY(0) scale(0.97)",
                    boxShadow: "0 10px 26px rgba(0,0,0,0.4)",
                  },

                  "@keyframes pulseReloadStrong": {
                    "0%": { boxShadow: "0 0 0 0 rgba(248, 173, 12, 0.8)" },
                    "50%": { boxShadow: "0 0 0 24px rgba(248, 173, 12, 0)" },
                    "100%": { boxShadow: "0 0 0 0 rgba(248, 173, 12, 0)" },
                  },
                  "@keyframes floatReload": {
                    "0%": { transform: "translateY(0) scale(1)" },
                    "50%": { transform: "translateY(-4px) scale(1.02)" },
                    "100%": { transform: "translateY(0) scale(1)" },
                  },
                }}
              >
                <span style={{ fontSize: "1.4rem", marginRight: 4 }}>↻</span>
                Vaciar caché y recargar ahora
              </Button>


            </Box>

            {/* Figura geométrica inferior */}
            <img
              src={bottomPattern}
              alt="Bottom Pattern"
              style={{
                position: "fixed",
                bottom: "-150px",
                left: 0,
                width: "100%",
                height: "auto",
              }}
            />
          </Box>
        </>
      );
    }
    return this.props.children as React.ReactElement | null;
  }
}
